import { IRule } from '@/classes/rules/IRule';
import { BooleanRule } from '@/classes/rules/BooleanRule';
import { ObjectRule } from '@/classes/rules/ObjectRule';
import { NumberRule } from '@/classes/rules/NumberRule';
import { MinMaxRule } from '@/classes/rules/MinMaxRule';
import { TextRule } from '@/classes/rules/TextRule';
import { EnumRule } from '@/classes/rules/EnumRule';
import { OptionsRule } from '@/classes/rules/OptionsRule';
import { ArrayRule } from '@/classes/rules/ArrayRule';

import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { ScenarioLoadExt } from '@/classes/scenario/load/ScenarioLoadExt';
import { LoadFileExt } from '@/classes/scenario/load/LoadFileExt';
import { LoadProfileExt } from '@/classes/scenario/load/LoadProfileExt';
import { LoadEnergyEfficiencyExt } from '@/classes/scenario/load/LoadEnergyEfficiencyExt';
import { OpeningHoursExt } from '@/classes/scenario/load/OpeningHoursExt';
import { OpeningHoursDayExt } from '@/classes/scenario/load/OpeningHoursDayExt';
import {
  LoadFileDurationRef,
  LoadFileReferenceUtils,
  LoadFileStepRef,
  LoadFileUnitRef
} from '@/dto/scenario/load/LoadReference';
import { DateRule } from '@/classes/rules/DateRule';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { InjectionModelRef } from '@/dto/scenario/ScenarioReference';

// RULES CONST =========================================================

// HOURS OPTIONS
const fromHoursOptions: number[] = [];
const toHoursOptions: number[] = [];
for (let i = 0; i < 24; i++) {
  fromHoursOptions.push(i);
  toHoursOptions.push(i);
}
// toHoursOptions.push(24);

// LOAD PROFILES
function getTypicalDayHoursRules(): IRule[] {
  return [
    new BooleanRule('isClosed').withDefaultValue(scenario => new DefaultValue(false)),
    new OptionsRule<number>('openingHour', fromHoursOptions).withDefaultValue(scenario => new DefaultValue(8)),
    new OptionsRule<number>('closingHour', toHoursOptions).withDefaultValue(scenario => new DefaultValue(21)),
    new MinMaxRule('nightPercentage', 0, 1, '%').withRequired(false)
  ];
}

function getBeforeDayHoursRules(dayName: string): IRule[] {
  return [
    new BooleanRule('isClosed').withDefaultValue(scenario => new DefaultValue(false)),
    new MinMaxRule('nightPercentage', 0, 1, '%')
      .withIsAvailable((scenario: any) => (
        scenario.load && scenario.load.profile &&
        scenario.load.profile.profileBeforeOpeningHours &&
        scenario.load.profile.profileBeforeOpeningHours[dayName] &&
        scenario.load.profile.profileBeforeOpeningHours[dayName].isClosed === true
      ) || false)
      .withFactor(100)
      .withDefaultValue(scenario => new DefaultValue(1) || null),
    new OptionsRule<number>('openingHour', fromHoursOptions)
      .withDefaultValue(scenario => new DefaultValue(1) || null),
    new OptionsRule<number>('closingHour', toHoursOptions)
      .withDefaultValue(scenario => new DefaultValue(1) || null)
  ];
}

function getAfterDayHoursRules(dayName: string): IRule[] {
  return [
    new BooleanRule('isClosed').withDefaultValue(scenario => new DefaultValue(false)),
    new MinMaxRule('nightPercentage', 0, 1, '%')
      .withIsAvailable((scenario: any) => (
        scenario.load && scenario.load.profile &&
        scenario.load.profile.profileAfterOpeningHours &&
        scenario.load.profile.profileAfterOpeningHours[dayName] &&
        scenario.load.profile.profileAfterOpeningHours[dayName].isClosed === true
      ) || false)
      .withFactor(100)
      .withDefaultValue(scenario => new DefaultValue(1) || null),
    new OptionsRule<number>('openingHour', fromHoursOptions)
      .withDefaultValue(scenario => new DefaultValue(1) || null),
    new OptionsRule<number>('closingHour', toHoursOptions)
      .withDefaultValue(scenario => new DefaultValue(1) || null)
  ];
}

const DayHoursCustomCheck = (val: OpeningHoursDayExt): boolean => {
  return !!val;
};

const profileAfterIsAvailable = (scenario: ScenarioExt) => (
  scenario.load && scenario.load.profile && scenario.load.profile.hasDifferentLoadProfileAfter
) || false;

const weekDayBeforeIsAvailable = (scenario: ScenarioExt) => (
  scenario.load && scenario.load.profile && scenario.load.profile.profileBeforeOpeningHours &&
  scenario.load.profile.profileBeforeOpeningHours.hasOnlyTypicalDay === false
) || false;

const weekDayAfterIsAvailable = (scenario: ScenarioExt) => (
  scenario.load && scenario.load.profile && scenario.load.profile.profileAfterOpeningHours &&
  scenario.load.profile.profileAfterOpeningHours.hasOnlyTypicalDay === false
) || false;

const beforeOpeningHoursExtRules: IRule[] = [
  new BooleanRule('hasOnlyTypicalDay'),
  new ObjectRule<OpeningHoursDayExt>('typicalDay', getTypicalDayHoursRules(), undefined, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('sunday', getBeforeDayHoursRules('sunday'), weekDayBeforeIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('monday', getBeforeDayHoursRules('monday'), weekDayBeforeIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('tuesday', getBeforeDayHoursRules('tuesday'), weekDayBeforeIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('wednesday', getBeforeDayHoursRules('wednesday'), weekDayBeforeIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('thursday', getBeforeDayHoursRules('thursday'), weekDayBeforeIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('friday', getBeforeDayHoursRules('friday'), weekDayBeforeIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('saturday', getBeforeDayHoursRules('saturday'), weekDayBeforeIsAvailable, DayHoursCustomCheck)
].map(rule => {
  if (rule instanceof ObjectRule) {
    return rule.withDefaultValue(scenario => new DefaultValue(new OpeningHoursDayExt()));
  }
  return rule;
});

const afterOpeningHoursExtRules: IRule[] = [
  new BooleanRule('hasOnlyTypicalDay'),
  new ObjectRule<OpeningHoursDayExt>('typicalDay', getTypicalDayHoursRules(), undefined, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('sunday', getAfterDayHoursRules('sunday'), weekDayAfterIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('monday', getAfterDayHoursRules('monday'), weekDayAfterIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('tuesday', getAfterDayHoursRules('tuesday'), weekDayAfterIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('wednesday', getAfterDayHoursRules('wednesday'), weekDayAfterIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('thursday', getAfterDayHoursRules('thursday'), weekDayAfterIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('friday', getAfterDayHoursRules('friday'), weekDayAfterIsAvailable, DayHoursCustomCheck),
  new ObjectRule<OpeningHoursDayExt>('saturday', getAfterDayHoursRules('saturday'), weekDayAfterIsAvailable, DayHoursCustomCheck)
].map(rule => {
  if (rule instanceof ObjectRule) {
    return rule.withDefaultValue(scenario => new DefaultValue(new OpeningHoursDayExt()));
  }
  return rule;
});

// LOAD RULES =========================================================
export const buildLoadRules = () => new ObjectRule<ScenarioLoadExt>('load', [
  // new NumberRule('loadPowerFactor'),
  new BooleanRule('hasUploadedLoad'),
  new BooleanRule('hasAdditionalConsumptionFromPVSystemAncillaryServices').withDefaultValue(() => new DefaultValue(false)),
  new MinMaxRule('additionalConsumptionPerDay', 0, undefined, 'kwh/day')
    .withIsAvailable(scenario => (
      scenario && scenario.load && scenario.load.hasAdditionalConsumptionFromPVSystemAncillaryServices === true
    ) || false),

  /* new NumberRule('additionalConsumption', undefined, (scenario: ScenarioExt) => !!(scenario.load
    && scenario.load.hasAdditionalConsumptionFromPVSystemAncillaryServices)
  ), */

  // File
  new ObjectRule<LoadFileExt>('file', [
    // new EnumRule('typeRef', LoadFileTypeRef), // Not used in interface for now
    new BooleanRule('hasFile'),
    new EnumRule('unitRef', LoadFileUnitRef),
    new EnumRule('stepRef', LoadFileStepRef),
    new DateRule('startDate'),
    new EnumRule('durationRef', LoadFileDurationRef),
    new TextRule('originalContent', undefined, undefined, val => {
      if (val === null) {
        return null;
      }
      const splitReg = /\n/; // used to match a breakline
      const numberReg = /^(\d+\.?\d*)$/; // match number.decimals
      const lines = val.split(splitReg);
      /**
       * We reverse-iterate because if there is an error, it's most likely in last lines the user has entered.
       * NOTE HERE : as i-- is directly in the evaluation, 'i' is evaluated as it is, then passed as 'i - 1'
       * Example : length of 1 ; i = 1 ; i evaluated as 1, so true ; i used as 1 - 1 = 0.
       * So on an array of length 1, it uses index 0, on an array of length 3, it uses indexes 2, 1, 0.
       */
      for (let i = lines.length; i--;) {
        if (lines[i] && !numberReg.test(lines[i])) {
          return false;
        }
      }
      return true;
    }),
    new MinMaxRule('correctionCoefficient', 0).withDefaultValue(scenario => new DefaultValue(1))
  ])
    .withIsAvailable(scenario => (scenario.load && scenario.load.hasUploadedLoad) || false)
    .withCustomCheck(val => {
      if (!val.originalContent || !val.stepRef || !val.durationRef) {
        return false;
      }
      const valuesPerHour = LoadFileReferenceUtils.getNumberOfValuesPerHour(val.stepRef);
      const hoursPerDur = LoadFileReferenceUtils.getNumberOfHoursPerDuration(val.durationRef);
      const lengthIsOk = hoursPerDur * valuesPerHour;
      const originalContent = val.originalContent;
      // check original content
      const splitReg = /\n/; // used to match a breakline
      const lines = originalContent.split(splitReg).filter((s) => s.length > 0);
      if (lines.length === lengthIsOk) {
        return true;
      }
      return false;
    })
    .withDefaultValue(scenario => new DefaultValue(new LoadFileExt())),

  // Profile
  new ObjectRule<LoadProfileExt>('profile', [

    new BooleanRule('hasMonthlyAverageSiteConsumption'),
    new NumberRule('annualAverageSiteConsumption', 'mwh/year')
      .withIsAvailable(scenario => (
        scenario.load && scenario.load.profile && scenario.load.profile &&
        scenario.load.profile.hasMonthlyAverageSiteConsumption === false) || false),
    new ArrayRule<MinMaxRule>('monthlyAverageSiteConsumption', scenario => (
      scenario.load && scenario.load.profile && scenario.load.profile &&
      scenario.load.profile.hasMonthlyAverageSiteConsumption === true) || false,
    new MinMaxRule('sample', 0, undefined, 'mwh'),
    val => val.length === 12
    ),

    new BooleanRule('hasDifferentLoadProfileAfter'),

    // Weekly Profile before
    new BooleanRule('profileBeforeHasSameConsumptionAllDayLong').withDefaultValue(scenario => new DefaultValue(false)),
    new MinMaxRule('profileBeforeNightConsumptionPercentage', 0, 1, '%')
      .withIsAvailable(scenario => (
        scenario.load && scenario.load.profile &&
        scenario.load.profile.profileBeforeHasSameConsumptionAllDayLong === false
      ) || false)
      .withFactor(100)
      .withDefaultValue(scenario => new DefaultValue(0.3)),
    new ObjectRule<OpeningHoursExt>('profileBeforeOpeningHours', beforeOpeningHoursExtRules)
      .withDefaultValue(scenario => new DefaultValue(new OpeningHoursExt())),

    // Weekly Profile after
    new BooleanRule('profileAfterHasSameConsumptionAllDayLong', profileAfterIsAvailable).withDefaultValue(scenario => new DefaultValue(false)),
    new MinMaxRule('profileAfterNightConsumptionPercentage', 0, 1, '%')
      .withIsAvailable(scenario => (
        profileAfterIsAvailable(scenario) &&
        scenario.load && scenario.load.profile &&
        scenario.load.profile.profileAfterHasSameConsumptionAllDayLong === false
      ) || false)
      .withFactor(100)
      .withDefaultValue(scenario => new DefaultValue(0.3)),
    new ObjectRule<OpeningHoursExt>('profileAfterOpeningHours', afterOpeningHoursExtRules, profileAfterIsAvailable)
      .withDefaultValue(scenario => new DefaultValue(new OpeningHoursExt()))
  ])
    .withIsAvailable(scenario => !!(scenario.load && (scenario.load.hasUploadedLoad === false)))
    .withDefaultValue(scenario => new DefaultValue(new LoadProfileExt())),

  // Energy Efficiency
  new BooleanRule('hasEnergyEfficiency').withDefaultValue(() => new DefaultValue(false)),
  new ObjectRule<LoadEnergyEfficiencyExt>('energyEfficiency', [
    new MinMaxRule('estimatedEnergySavings', 0, undefined, '%')
      .withDefaultValue(scenario => new DefaultValue(0))
      .withFactor(100),
    // new DateRule('startDate', ninteenEighty),
    new MinMaxRule('investmentCost', 0, undefined, '$')
      .withDefaultValue(scenario => new DefaultValue(0))
  ])
    .withIsAvailable(scenario => (scenario.load && scenario.load.hasEnergyEfficiency) || false)
    .withDefaultValue(scenario => new DefaultValue(new LoadEnergyEfficiencyExt()))
])
  .withIsAvailable(scenario => !scenario.hasInjectionType(InjectionModelRef.total_injection))
  .withDefaultValue(scenario => new DefaultValue(new ScenarioLoadExt()));
